import { HttpClient } from "../../services/api/httpclient.service";
import testExercises from "../../mock/test-exercises.json";
import testAnswers from "../../mock/test-answers.json";
import testSummary from "../../mock/test-summary.json";

export interface EnrichedExerciseItem {
  id: number;
  filename: string;
  title?: string;
  level_units?: number;
  topic_name?: string;
  difficulty_name?: string;
  is_exercise_approved: boolean;
  is_solution_approved: boolean;
  upload_date: string;
}

export interface SearchExerciseDBResponse {
  results: EnrichedExerciseItem[];
  total: number;
  page?: number;
  page_size?: number;
}

export class SearchExerciseService extends HttpClient {
  constructor() {
    super();
  }

  public async getTopics(levelId: number | false = false, withExercises: boolean = false) {
    try {
      const params = new URLSearchParams();
      if (levelId) params.append("level_id", levelId.toString());
      params.append("with_exercises", String(withExercises));

      const query = params.toString() ? `?${params.toString()}` : "";
      const response = await this.get(`/data/get_topics${query}`);
      return response;
    } catch (error) {
      console.error("Error fetching topics:", error);
      return [];
    }
  }

  public async getSubTopics(topicId: number | false = false, levelId?: number) {
    try {
      const params = new URLSearchParams();
      if (topicId) params.append("topic_id", topicId.toString());
      if (levelId) params.append("level_id", levelId.toString());

      const query = params.toString() ? `?${params.toString()}` : "";
      const response = await this.get(`/data/get_sub_topics${query}`);
      return response;
    } catch (error) {
      console.error("Error fetching sub-topics:", error);
      return [];
    }
  }

  public async getLevels(withExercises: boolean = false) {
    try {
      const query = `?with_exercises=${withExercises}`;
      const response = await this.get(`/data/get_levels${query}`);
      return response;
    } catch (error) {
      console.error("Error fetching levels:", error);
      return [];
    }
  }

  public async getDifficulties(levelId: number | false = false, topicId: number | false = false, withExercises: boolean = false) {
    try {
      const params = new URLSearchParams();
      if (levelId) params.append("level_id", levelId.toString());
      if (topicId) params.append("topic_id", topicId.toString());
      params.append("with_exercises", String(withExercises));

      const query = params.toString() ? `?${params.toString()}` : "";
      const response = await this.get(`/data/get_difficulties${query}`);
      return response;
    } catch (error) {
      console.error("Error fetching difficulties:", error);
      return [];
    }
  }

  public async searchExerciseInDB(
    page: number,
    pageSize: number,
    search?: string,
    status?: string,
    level_id?: string | number,
    topic_id?: string | number,
    difficulty_id?: string | number,
    isForLesson?: boolean
  ): Promise<SearchExerciseDBResponse> {
    try {
      const parseId = (val?: string | number) => {
        if (val === undefined || val === null || val === '') return undefined;
        return typeof val === 'number' ? val : parseInt(val, 10);
      };

      const body = {
        page,
        page_size: pageSize,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
        ...(parseId(level_id) !== undefined ? { level_id: parseId(level_id) } : {}),
        ...(parseId(topic_id) !== undefined ? { topic_id: parseId(topic_id) } : {}),
        ...(parseId(difficulty_id) !== undefined ? { difficulty_id: parseId(difficulty_id) } : {}),
        ...(isForLesson ? { is_for_lesson: isForLesson } : {}),
      };

      const response = await this.post<SearchExerciseDBResponse>('/data/search_exercise_in_db', body);
      return response;
    } catch (error) {
      console.error("Error searching exercises in DB:", error);
      return { results: [], total: 0 };
    }
  }

  public async deleteExerciseFromDB(exericse_id: number) {
    try {
      return await this.delete(`/data/delete_exercise_from_db/${exericse_id}`);
    } catch (error) {
      console.error("Error deleting exercise from DB:", error);
      throw error;
    }
  }

  public async getExerciseCounter() {
    try {
      const response = await this.get(`/data/get_exercise_counter`);
      return response;
    } catch (error) {
      console.error("Error fetching get_difficulties:", error);
      return [];
    }
  }

  public async updateExercise(
    exercise_id: number,
    payload: {
      level_id?: number;
      topic_id?: number;
      difficulty_id?: number;
      is_exercise_approved?: boolean;
      is_solution_approved?: boolean;
      solution_content?: string;
    }
  ) {
    try {
      const response = await this.put(`/data/update_exercise/${exercise_id}`, payload);
      return response;
    } catch (error: any) {
      console.error('Exercise update error:', error);
      throw error;
    }
  }

  public async getChartsByExerciseId(exercise_id: number) {
    try {
      const response = await this.get(`/data/get_question_charts/${exercise_id}`);
      return response;
    } catch (error) {
      console.error("Error fetching get_difficulties:", error);
      return [];
    }
  }

  public async getExerciseTitles(ids: number[]) {
    try {
      const query = ids.map(id => `ids=${id}`).join('&');
      const response = await this.get(`/data/titles?${query}`);
      return response;
    } catch (error) {
      console.error("Error fetching exercise titles:", error);
      return [];
    }
  }

  public async getExerciseBooks() {
    try {
      const response = await this.get(`/data/get_books`);
      return response;
    } catch (error) {
      console.error("Error fetching books:", error);
      return [];
    }
  }

  public async getLuBooks() {
    try {
      const response = await this.get(`/data/get_lu_books`);
      return response;
    } catch (error) {
      console.error("Error fetching books:", error);
      return [];
    }
  }

  public async searchExerciseByBook(payload: { book_id: number; page_number: number; exercise_number: number; }) {
    try {
      const response = await this.post(`/data/search_exercise_by_book`, payload);
      return response;
    } catch (error) {
      console.error("Error search exercise by book:", error);
      return [];
    }
  }

  public async searchExerciseByImage(imageFile: File, page: number = 1, pageSize: number = 10) {
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      formData.append('page', page.toString());
      formData.append('page_size', pageSize.toString());

      const response = await this.post(`/data/search_exercise_by_image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response;
    } catch (error) {
      console.error("Error searching exercise by image:", error);
      throw error;
    }
  }
  public async getFixedSolutionFromLLM(payload: { exercise_id: number; existing_solution: string; user_prompt: string; }) {
    try {
      const response = await this.post(`/data/get_fixed_solution_from_LLM`, payload);
      return response;
    } catch (error) {
      console.error("Error search exercise by book:", error);
      return [];
    }
  }

  public async getExercise(exercise_id: number) {
    try {
      const response = await this.get(`/data/get_exercise_content/${exercise_id}`);
      return response;
    } catch (error: any) {
      console.error('Get exercise content API error:', error.response?.data || error.message);
      throw error;
    }

  }

  public async getLiteExerciseContent(exercise_id: number) {
    try {
      const response = await this.get(`/data/get_lite_exercise_content/${exercise_id}`);
      return response;
    } catch (error: any) {
      console.error('Get lite exercise content API error:', error.response?.data || error.message);
      throw error;
    }

  }

  public async createQuestionChart(payload: { exercise_id: number; user_prompt: string; question_id: number; }) {
    try {
      const response = await this.post(`/data/create-question-chart/`, payload);
      return response;
    } catch (error) {
      console.error("Error creating question chart:", error);
      throw error;
    }
  }

  public async createExerciseByImage(formData: FormData) {
    try {
      const response = await this.post('/data/create_exercise_by_image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response;
    } catch (error) {
      console.error("Error deleting exercise from Azure:", error);
      throw error;
    }
  }

  async get_self_test_exercise_ids(payload: { topic_id: number; level_id: number }) {
    return testExercises.find(
      (item) =>
        item.test_info.topic_id === payload.topic_id &&
        item.test_info.level_id === payload.level_id
    );
  }
  async getExercise_test_answers(exercise_id: number) {
    return {
      exercise_id,
      exercise_answers: testAnswers[exercise_id] || [],
    };
  }

  async update_user_test_exercise_answer(payload: {
    user_id: number;
    exercise_id: number;
    selected_answer_id: number;
  }) {
    await new Promise((r) => setTimeout(r, 300));
    return { success: true };
  }

  async get_user_test_exercise_summary(payload: { user_id: number; test_id: number }) {
    return testSummary[payload.test_id] || null;
  }
}
